import type { PDFDocumentLoadingTask, PDFDocumentProxy } from "pdfjs-dist"

import { http } from "@/lib/http"

/**
 * pdf.js document loader with a bounded LRU cache.
 *
 * A parsed PDFDocumentProxy holds font tables, xref data and worker
 * bindings — for a 1100-page label that is measured in tens of MB. Caching
 * every document a reviewer opens without a ceiling turns into a slow leak
 * across a shift. We keep at most `MAX_DOCS` documents; the least-recently
 * used one is destroyed (freeing its worker resources) when a new URL
 * displaces it. Two documents is enough for the common case of FPL + master
 * open side-by-side, and small enough that memory does not climb.
 *
 * We hand pdf.js raw bytes (Uint8Array) rather than a URL. Two reasons:
 *
 *   1. The `/api/v1/documents/{id}/file` endpoint requires an Authorization
 *      header; pdf.js's own fetch cannot see localStorage, so a URL load
 *      would 401. Going through `http.bytes()` reuses our authenticated
 *      transport (bearer token + silent refresh loop).
 *   2. Blob-URL lifetime is a whole class of bug — pdf.js reports
 *      "unexpected server response (0)" for a URL that gets revoked while
 *      a query entry is still live. Bytes have no lifetime; the ArrayBuffer
 *      is owned by pdf.js from the moment we hand it over.
 */

const MAX_DOCS = 4

/** The task is kept alongside the document: only it can release the worker's copy. */
interface CacheEntry {
  task: Promise<PDFDocumentLoadingTask>
  doc: Promise<PDFDocumentProxy>
}

let workerConfigured = false
const docs = new Map<string, CacheEntry>()

async function configureWorker(): Promise<typeof import("pdfjs-dist")> {
  const pdfjs = await import("pdfjs-dist")
  if (!workerConfigured) {
    const workerUrl = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString()
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
    workerConfigured = true
  }
  return pdfjs
}

async function destroyEntry(entry: CacheEntry): Promise<void> {
  try {
    await entry.doc
  } catch {
    // The load failed; the task still has to be released.
  }
  try {
    const task = await entry.task
    if (!task.destroyed) await task.destroy()
  } catch {
    // The task never got as far as existing; nothing holds the file.
  }
}

function touch(url: string, entry: CacheEntry): void {
  // Re-insert to move to the end — Map preserves insertion order, so the
  // first key is the LRU one when we need to evict.
  docs.delete(url)
  docs.set(url, entry)
}

export async function getPdfDocument(url: string): Promise<PDFDocumentProxy> {
  const cached = docs.get(url)
  if (cached) {
    touch(url, cached)
    return cached.doc
  }

  const task = (async () => {
    const pdfjs = await configureWorker()
    // Only auth-required paths on our own origin need the http.bytes wrapper.
    // Anything absolute (or not under /api/) is loaded the pdf.js-native way.
    const needsAuth = url.startsWith("/api/") || url.startsWith("/health")
    if (!needsAuth) return pdfjs.getDocument({ url })
    const bytes = await http.bytes(url)
    // pdf.js may mutate the buffer; hand it a fresh copy so a cache hit
    // months from now still sees pristine data (its consumer could also
    // create a new task on the same doc, which detaches the buffer).
    return pdfjs.getDocument({ data: new Uint8Array(bytes) })
  })()

  const entry: CacheEntry = { task, doc: task.then((t) => t.promise) }
  docs.set(url, entry)
  // Evict the oldest entries synchronously — even before this load resolves —
  // so a burst of new URLs cannot drive the cache past the cap.
  while (docs.size > MAX_DOCS) {
    const oldest = docs.keys().next().value
    if (oldest === undefined || oldest === url) break
    const evicted = docs.get(oldest)
    docs.delete(oldest)
    if (evicted) void destroyEntry(evicted)
  }

  try {
    return await entry.doc
  } catch (err) {
    // A failed load is not cached: the next caller should get a fresh attempt
    // rather than the same rejection forever.
    docs.delete(url)
    void destroyEntry(entry)
    throw err
  }
}
